import { FC, ReactNode, useId, useState } from 'react';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';
import { AngleIcon } from 'shared/icons';
import { IconType } from 'shared/icons/SvgIcon';
import { cn } from 'shared/utils/cn';

interface PaperProps {
  title?: string;
  headerActions?: ReactNode;
  size?: 'md' | 'lg';
  icon?: IconType;
  className?: string;
  wrapperClassName?: string;
  titleClassName?: string;
  backgroundColor?: 'white' | 'primary';
  collapseOnMobile?: boolean;
  initialCollapsed?: boolean;
  children: ReactNode;
}

export const Paper: FC<PaperProps> = ({
  title,
  headerActions,
  size = 'md',
  icon: Icon,
  className,
  wrapperClassName,
  titleClassName,
  backgroundColor = 'white',
  collapseOnMobile = false,
  initialCollapsed = true,
  children,
}) => {
  const isMobile = useBreakpoint('lg');
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const contentId = useId();
  const canCollapse = collapseOnMobile && isMobile;
  const heading = (
    <>
      <span className="flex min-w-0 items-center gap-3">
        {Icon && <Icon className="h-5 w-5 shrink-0 text-black/60" />}
        <span className={titleClassName}>{title}</span>
      </span>
      {canCollapse && (
        <AngleIcon className={cn('h-4 w-4 shrink-0 text-black/60', !collapsed && 'rotate-180')} />
      )}
    </>
  );

  return (
    <section
      className={cn(
        'shadow-paper min-w-0 rounded-2xl p-5 py-6 md:p-8',
        size === 'lg' && 'lg:px-10',
        backgroundColor === 'primary' ? 'bg-primary-100' : 'bg-white',
        className,
      )}
    >
      {(title || Icon) && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="min-w-0 text-xl leading-7 font-medium md:text-2xl">
            {canCollapse ? (
              <button
                type="button"
                aria-expanded={!collapsed}
                aria-controls={contentId}
                onClick={() => setCollapsed((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                {heading}
              </button>
            ) : (
              <span className="flex items-center justify-between gap-3">{heading}</span>
            )}
          </h2>
          {headerActions}
        </div>
      )}
      <div
        id={contentId}
        hidden={canCollapse && collapsed}
        className={cn((title || Icon) && 'pt-6', wrapperClassName)}
      >
        {children}
      </div>
    </section>
  );
};
